import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PdfViewer } from '../PdfViewer/PdfViewer';
import { Loader } from '../Loader/Loader';
import { fetchCopilotFile, fileErrorMessage } from './copilotFileApi';
import { ReactComponent as ArrowIcon } from '../CopilotButtonAssistantUI/assets/hide-arrows.svg';
import styles from './CopilotMinButton.module.scss';

const CLOSE_ANIMATION_MS = 300;

// Side panel (left of the chat, same slot as PublicationsPanel) that previews
// a PDF Copilot made. The file is fetched with auth through the product API
// and rendered from the Blob; downloading stays on the file card in the chat.
//
// Closing is animated, so it is a two-step: the Hide button (or the parent,
// via `closing`) starts the slide-out and `onClose` fires once it has played.
const ReportPanel = ({ reportRef, closing = false, onClose }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState(null); // { blob, fileName }
  const [error, setError] = useState(null);
  const [hideRequested, setHideRequested] = useState(false);
  const isClosing = closing || hideRequested;

  useEffect(() => {
    if (!isClosing) return undefined;
    const timer = setTimeout(onClose, CLOSE_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isClosing, onClose]);

  useEffect(() => {
    const controller = new AbortController();
    setReport(null);
    setError(null);

    fetchCopilotFile(reportRef, { signal: controller.signal })
      .then(setReport)
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setError(err);
      });

    return () => controller.abort();
  }, [reportRef]);

  return (
    <aside
      className={`${styles.publicationsPanel}${isClosing ? ` ${styles.publicationsPanelClosing}` : ''}`}
      aria-label={t('Report preview')}
    >
      <div className={styles.panelTopRow}>
        <span className={styles.panelSectionLabel}>{t('Report')}</span>
        <button
          type="button"
          className={styles.panelHideBtn}
          onClick={() => setHideRequested(true)}
          aria-label={t('Hide report panel')}
        >
          {t('Hide')}
          <ArrowIcon className={styles.panelHideArrow} aria-hidden="true" />
        </button>
      </div>

      <div className={`${styles.panelBody} ${styles.reportPanelBody}`}>
        {error ? (
          <p className={styles.panelEmpty} role="alert">
            {fileErrorMessage(error, t)}
          </p>
        ) : report === null ? (
          <div
            className={`${styles.panelLoading} ${styles.reportPanelLoading}`}
            role="status"
            aria-live="polite"
            aria-label={t('Loading report…')}
          >
            <Loader className={styles.reportPanelLoader} />
          </div>
        ) : (
          <PdfViewer
            file={report.blob}
            onFailed={() => setError(new Error('render'))}
          />
        )}
      </div>
    </aside>
  );
};

export default ReportPanel;
